import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUp, ArrowDown, Search } from "lucide-react";
import { costMatrixApi } from "@/features/finances/api/finances";
import { formatAmount } from "@/utils/formatAmount";
import { PageLoader } from "@/components/ui/Spinner";
import type { CostMatrixGroupRow } from "@/features/finances/types";

type GSort = "name" | "salary" | "other" | "total";

function num(v: string | null): number {
  return parseFloat(v ?? "0") || 0;
}
function rowTotal(r: CostMatrixGroupRow): number {
  return num(r.salary_cost) + num(r.other_cost);
}

function Pln({ value }: { value: string | number }) {
  return <>{formatAmount(typeof value === "number" ? value.toFixed(2) : value)} <span className="text-[10px] font-normal text-gray-400">PLN</span></>;
}

/** Macierz kosztów zwinięta do grup zleceń — drobnica łączy się w jeden wiersz na grupę. */
export function GroupedCostMatrix({ year, month }: { year: number; month?: number }) {
  const showMonthCols = month !== undefined;
  const { data, isLoading } = useQuery({
    queryKey: ["order-cost-matrix-grouped", year, month],
    queryFn: () => costMatrixApi.getGrouped(year, month),
  });

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<GSort>("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function ts(field: GSort) {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir(field === "name" ? "asc" : "desc"); }
  }

  const rows = useMemo(() => {
    let r = data?.rows ?? [];
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter((x) => x.group_name.toLowerCase().includes(q) || x.group_short_name.toLowerCase().includes(q));
    }
    return [...r].sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") cmp = (a.group_short_name || a.group_name).localeCompare(b.group_short_name || b.group_name, "pl");
      else if (sortField === "salary") cmp = num(a.salary_cost) - num(b.salary_cost);
      else if (sortField === "other") cmp = num(a.other_cost) - num(b.other_cost);
      else cmp = rowTotal(a) - rowTotal(b);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [data, search, sortField, sortDir]);

  const sumSalary = useMemo(() => rows.reduce((s, r) => s + num(r.salary_cost), 0), [rows]);
  const sumOther = useMemo(() => rows.reduce((s, r) => s + num(r.other_cost), 0), [rows]);
  const sumProjects = useMemo(() => rows.reduce((s, r) => s + r.project_count, 0), [rows]);

  if (isLoading) return <PageLoader />;

  const SortHead = ({ field, children, right }: { field: GSort; children: React.ReactNode; right?: boolean }) => (
    <th
      className={`px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none ${right ? "text-right" : "text-left"}`}
      onClick={() => ts(field)}
    >
      {children}{sortField === field ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Szukaj + sortowanie (mobile) */}
      <div className="bg-white dark:bg-gray-800 p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[160px]">
          <Search size={15} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" placeholder="Szukaj grupy / symbolu..."
            className="w-full border border-gray-300 dark:border-gray-600 rounded pl-8 pr-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="lg:hidden flex items-center gap-2">
          <select
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
            value={sortField} onChange={(e) => setSortField(e.target.value as GSort)}
          >
            <option value="name">Grupa</option>
            {showMonthCols && <option value="salary">Koszty płac</option>}
            <option value="other">Pozostałe koszty</option>
            <option value="total">Łącznie</option>
          </select>
          <button onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))} className="w-9 h-9 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 text-[var(--color-primary)]">
            {sortDir === "asc" ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
          </button>
        </div>
      </div>

      {/* Podsumowanie */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
        {([
          { label: "Grupy zleceń", value: String(rows.length) },
          { label: "Przedsięwzięcia", value: String(sumProjects) },
          ...(showMonthCols ? [{ label: "Koszty płac", value: `${formatAmount(sumSalary.toFixed(2))} PLN` }] : []),
          { label: "Pozostałe koszty", value: `${formatAmount(sumOther.toFixed(2))} PLN` },
          { label: "Łącznie", value: `${formatAmount((sumSalary + sumOther).toFixed(2))} PLN` },
        ] as { label: string; value: string }[]).map(({ label, value }) => (
          <div key={label} className="bg-white dark:bg-gray-800 px-3 py-2 sm:px-4 sm:py-2.5 flex flex-col sm:min-w-[100px]">
            <span className="text-[10px] sm:text-xs font-mono uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
            <span className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 tabular-nums">{value}</span>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 py-12 text-center text-sm text-gray-400 dark:text-gray-500">
          Brak grup dla wybranych kryteriów.
        </div>
      ) : (
        <>
          {/* Tabela — desktop */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <SortHead field="name">Symbol / grupa</SortHead>
                  <th className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 text-right w-28">Przedsięwzięć</th>
                  {showMonthCols && <SortHead field="salary" right>Koszty płac</SortHead>}
                  <SortHead field="other" right>Pozostałe</SortHead>
                  <SortHead field="total" right>Łącznie</SortHead>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.group_id ?? "none"} className={`border-b border-gray-100 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 ${!row.is_active ? "opacity-60" : ""}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.group_short_name || "—"}</span>
                        <span className="text-gray-900 dark:text-gray-100">{row.group_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">{row.project_count}</td>
                    {showMonthCols && (
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-blue-700 dark:text-blue-400"><Pln value={row.salary_cost ?? "0"} /></td>
                    )}
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100"><Pln value={row.other_cost} /></td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-bold text-gray-900 dark:text-gray-100"><Pln value={rowTotal(row)} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-800/60 border-t-2 border-gray-200 dark:border-gray-600 font-bold text-gray-900 dark:text-gray-100">
                  <td className="px-4 py-3">Razem ({rows.length})</td>
                  <td className="px-4 py-3 text-right tabular-nums">{sumProjects}</td>
                  {showMonthCols && <td className="px-4 py-3 text-right tabular-nums text-blue-700 dark:text-blue-400"><Pln value={sumSalary} /></td>}
                  <td className="px-4 py-3 text-right tabular-nums"><Pln value={sumOther} /></td>
                  <td className="px-4 py-3 text-right tabular-nums"><Pln value={sumSalary + sumOther} /></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Karty — mobile */}
          <div className="lg:hidden flex flex-col gap-2">
            {rows.map((row) => (
              <div key={row.group_id ?? "none"} className={`bg-white dark:bg-gray-800 p-3 ${!row.is_active ? "opacity-60" : ""}`}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.group_short_name || "—"}</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{row.group_name}</span>
                  <span className="text-[10px] text-gray-400">· {row.project_count} przeds.</span>
                </div>
                <div className={`grid ${showMonthCols ? "grid-cols-3" : "grid-cols-2"} gap-2 pt-2 border-t border-gray-100 dark:border-gray-700`}>
                  {showMonthCols && (
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Płace</span>
                      <span className="tabular-nums font-semibold text-blue-700 dark:text-blue-400 text-sm truncate"><Pln value={row.salary_cost ?? "0"} /></span>
                    </div>
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Pozostałe</span>
                    <span className="tabular-nums font-semibold text-gray-900 dark:text-gray-100 text-sm truncate"><Pln value={row.other_cost} /></span>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">Łącznie</span>
                    <span className="tabular-nums font-bold text-gray-900 dark:text-gray-100 text-sm truncate"><Pln value={rowTotal(row)} /></span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
